import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Row,
  Table,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { RefreshCw, Save, UserPlus } from "lucide-react";
import { toast } from "react-toastify";

import {
  adminCreateUser,
  adminGetUsers,
  adminUpdateUser,
  resetAdminUserCreate,
  resetAdminUserUpdate,
} from "../../actions/userActions";
import Loader from "../../components/Loader";
import Message from "../../components/Message";
import Meta from "../../components/Meta";

const PAGE_SIZE = 20;
const EMPTY_CREATE_FORM = {
  title: "",
  name: "",
  lastName: "",
  email: "",
  password: "",
  roles: ["user"],
};

const ROLE_OPTIONS = [
  { value: "user", label: "User" },
  { value: "customer", label: "Customer" },
  { value: "store_manager", label: "Store Manager" },
  { value: "admin", label: "Admin" },
  { value: "super_admin", label: "Super Admin" },
  { value: "editor", label: "Editor" },
];

function safeText(value = "") {
  return String(value ?? "").trim();
}

function getUserId(user = {}) {
  return safeText(user.id || user._id);
}

function normalizeRoles(value) {
  const rawRoles = Array.isArray(value)
    ? value
    : safeText(value)
        .split(",")
        .map((role) => role.trim());

  return [
    ...new Set(
      rawRoles
        .map((role) => safeText(role).toLowerCase())
        .filter((role) => ROLE_OPTIONS.some((option) => option.value === role)),
    ),
  ];
}

function getDisplayName(user = {}) {
  return [user.title, user.name, user.lastName]
    .map(safeText)
    .filter(Boolean)
    .join(" ");
}

function formatDate(value) {
  if (!value) return "-";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) return "-";

  return date.toLocaleDateString("en-GB");
}

export default function AdminUsersScreen() {
  const dispatch = useDispatch();

  const listState = useSelector((state) => state.adminUserList || {});
  const createState = useSelector((state) => state.adminUserCreate || {});
  const updateState = useSelector((state) => state.adminUserUpdate || {});

  const users = Array.isArray(listState.users) ? listState.users : [];
  const totalPages = Math.max(1, Number(listState.pages || 1));
  const currentPage = Math.max(1, Number(listState.page || 1));

  const [page, setPage] = useState(currentPage);
  const [createForm, setCreateForm] = useState(EMPTY_CREATE_FORM);
  const [editedRoles, setEditedRoles] = useState({});
  const [savingUserId, setSavingUserId] = useState("");

  const loadUsers = useCallback(() => {
    dispatch(adminGetUsers({ page, limit: PAGE_SIZE }));
  }, [dispatch, page]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!updateState.success) return;

    dispatch(resetAdminUserUpdate());
  }, [dispatch, updateState.success]);

  useEffect(() => {
    if (!createState.success) return;

    dispatch(resetAdminUserCreate());
  }, [createState.success, dispatch]);

  const roleLabels = useMemo(
    () =>
      ROLE_OPTIONS.reduce((labels, option) => {
        labels[option.value] = option.label;
        return labels;
      }, {}),
    [],
  );

  const getRolesForUser = useCallback(
    (user) => {
      const userId = getUserId(user);

      if (editedRoles[userId]) {
        return normalizeRoles(editedRoles[userId]);
      }

      return normalizeRoles(user.roles ?? user.role);
    },
    [editedRoles],
  );

  const toggleRole = useCallback(
    (user, role) => {
      const userId = getUserId(user);
      if (!userId) return;

      const currentRoles = getRolesForUser(user);
      const nextRoles = currentRoles.includes(role)
        ? currentRoles.filter((item) => item !== role)
        : [...currentRoles, role];

      setEditedRoles((current) => ({
        ...current,
        [userId]: nextRoles,
      }));
    },
    [getRolesForUser],
  );

  const toggleCreateRole = useCallback((role) => {
    setCreateForm((current) => {
      const roles = normalizeRoles(current.roles);
      const nextRoles = roles.includes(role)
        ? roles.filter((item) => item !== role)
        : [...roles, role];

      return {
        ...current,
        roles: nextRoles,
      };
    });
  }, []);

  const handleCreateChange = useCallback((event) => {
    const { name, value } = event.target;

    setCreateForm((current) => ({
      ...current,
      [name]: value,
    }));
  }, []);

  const handleCreateUser = useCallback(
    async (event) => {
      event.preventDefault();

      const roles = normalizeRoles(createForm.roles);

      if (!roles.length) {
        toast.error("Choose at least one role for this user.");
        return;
      }

      const createdUser = await dispatch(
        adminCreateUser({
          ...createForm,
          roles,
        }),
      );

      if (!createdUser) {
        toast.error(createState.error || "We could not create this user.");
        return;
      }

      toast.success("User created.");
      setCreateForm(EMPTY_CREATE_FORM);
      loadUsers();
    },
    [createForm, createState.error, dispatch, loadUsers],
  );

  const saveRoles = useCallback(
    async (user) => {
      const userId = getUserId(user);
      const roles = getRolesForUser(user);

      if (!userId || !roles.length) {
        toast.error("Choose at least one role for this user.");
        return;
      }

      setSavingUserId(userId);

      const updatedUser = await dispatch(adminUpdateUser(userId, { roles }));

      setSavingUserId("");

      if (!updatedUser) {
        toast.error(updateState.error || "We could not update this user.");
        return;
      }

      setEditedRoles((current) => {
        const next = { ...current };
        delete next[userId];
        return next;
      });

      toast.success("User roles updated.");
      loadUsers();
    },
    [dispatch, getRolesForUser, loadUsers, updateState.error],
  );

  return (
    <Container className="py-4">
      <Meta
        title="Admin users | Academy Costumes London"
        description="Manage Academy Costumes London user roles."
        robots="noindex, nofollow"
      />

      <Row className="align-items-center g-3 mb-3">
        <Col>
          <h1 className="mb-1">Users</h1>
          <div className="text-muted">Manage account roles and access.</div>
        </Col>

        <Col md="auto">
          <Button
            type="button"
            variant="outline-dark"
            className="d-inline-flex align-items-center gap-2"
            disabled={Boolean(listState.loading)}
            onClick={loadUsers}
          >
            <RefreshCw size={16} />
            Refresh
          </Button>
        </Col>
      </Row>

      {updateState.error ? (
        <Message variant="danger">{updateState.error}</Message>
      ) : null}

      {listState.error ? (
        <Message variant="danger">{listState.error}</Message>
      ) : null}

      {createState.error ? (
        <Message variant="danger">{createState.error}</Message>
      ) : null}

      {listState.loading ? <Loader /> : null}

      <Card className="shadow-sm mb-4">
        <Card.Body>
          <Form onSubmit={handleCreateUser}>
            <Row className="g-3 align-items-end">
              <Col md={2}>
                <Form.Group controlId="createUserTitle">
                  <Form.Label>Title</Form.Label>
                  <Form.Select
                    name="title"
                    value={createForm.title}
                    onChange={handleCreateChange}
                    disabled={Boolean(createState.loading)}
                  >
                    <option value="">None</option>
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Ms">Ms</option>
                    <option value="Miss">Miss</option>
                    <option value="Dr">Dr</option>
                    <option value="Prof">Prof</option>
                  </Form.Select>
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group controlId="createUserName">
                  <Form.Label>First name</Form.Label>
                  <Form.Control
                    name="name"
                    value={createForm.name}
                    onChange={handleCreateChange}
                    disabled={Boolean(createState.loading)}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={3}>
                <Form.Group controlId="createUserLastName">
                  <Form.Label>Last name</Form.Label>
                  <Form.Control
                    name="lastName"
                    value={createForm.lastName}
                    onChange={handleCreateChange}
                    disabled={Boolean(createState.loading)}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group controlId="createUserEmail">
                  <Form.Label>Email</Form.Label>
                  <Form.Control
                    type="email"
                    name="email"
                    value={createForm.email}
                    onChange={handleCreateChange}
                    disabled={Boolean(createState.loading)}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={4}>
                <Form.Group controlId="createUserPassword">
                  <Form.Label>Temporary password</Form.Label>
                  <Form.Control
                    type="password"
                    name="password"
                    value={createForm.password}
                    onChange={handleCreateChange}
                    autoComplete="new-password"
                    disabled={Boolean(createState.loading)}
                    required
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Label>Roles</Form.Label>
                <div className="d-flex flex-wrap gap-3">
                  {ROLE_OPTIONS.map((option) => (
                    <Form.Check
                      key={`create-${option.value}`}
                      type="checkbox"
                      id={`create-user-${option.value}`}
                      label={option.label}
                      checked={normalizeRoles(createForm.roles).includes(
                        option.value,
                      )}
                      onChange={() => toggleCreateRole(option.value)}
                      disabled={Boolean(createState.loading)}
                    />
                  ))}
                </div>
              </Col>

              <Col md={2} className="text-md-end">
                <Button
                  type="submit"
                  variant="dark"
                  className="d-inline-flex align-items-center gap-2"
                  disabled={Boolean(createState.loading)}
                >
                  <UserPlus size={16} />
                  {createState.loading ? "Creating" : "Create"}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      <Table striped bordered hover responsive className="align-middle">
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Status</th>
            <th>Created</th>
            <th>Roles</th>
            <th className="text-end">Actions</th>
          </tr>
        </thead>

        <tbody>
          {users.length ? (
            users.map((user) => {
              const userId = getUserId(user);
              const roles = getRolesForUser(user);
              const isDirty = Boolean(editedRoles[userId]);
              const isSaving = savingUserId === userId;
              const isDisabled = Boolean(user.isDeleted || user.isBlocked);

              return (
                <tr key={userId}>
                  <td>
                    <strong>{getDisplayName(user) || "User"}</strong>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <Badge
                      bg={isDisabled ? "warning" : "success"}
                      text={isDisabled ? "dark" : undefined}
                    >
                      {isDisabled ? "Restricted" : user.status || "active"}
                    </Badge>
                  </td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>
                    <div className="d-flex flex-wrap gap-3">
                      {ROLE_OPTIONS.map((option) => (
                        <Form.Check
                          key={`${userId}-${option.value}`}
                          type="checkbox"
                          id={`user-${userId}-${option.value}`}
                          label={roleLabels[option.value]}
                          checked={roles.includes(option.value)}
                          onChange={() => toggleRole(user, option.value)}
                          disabled={isSaving}
                        />
                      ))}
                    </div>
                  </td>
                  <td className="text-end">
                    <Button
                      type="button"
                      variant={isDirty ? "dark" : "outline-dark"}
                      size="sm"
                      className="d-inline-flex align-items-center gap-2"
                      disabled={!isDirty || isSaving || !roles.length}
                      onClick={() => saveRoles(user)}
                    >
                      <Save size={14} />
                      {isSaving ? "Saving" : "Save"}
                    </Button>
                  </td>
                </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={6} className="text-center text-muted py-4">
                No users found.
              </td>
            </tr>
          )}
        </tbody>
      </Table>

      <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
        <span className="text-muted">
          Page {currentPage} of {totalPages}
        </span>

        <div className="d-flex gap-2">
          <Button
            type="button"
            variant="outline-dark"
            disabled={page <= 1 || Boolean(listState.loading)}
            onClick={() => setPage((value) => Math.max(1, value - 1))}
          >
            Previous
          </Button>
          <Button
            type="button"
            variant="outline-dark"
            disabled={page >= totalPages || Boolean(listState.loading)}
            onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
          >
            Next
          </Button>
        </div>
      </div>
    </Container>
  );
}
